// Waypoints the user saved — the specific block being worked this month.
//
// The built-in list in waypoints.ts covers the REGIONS this company works; this
// covers the finer thing it cannot know, like "Walker Ridge Blk 3, the job we are
// on". It is deliberately an EXTRA, never a step you must complete: the console is
// fully usable having saved nothing.
//
// A saved waypoint has the same shape as a built-in one, because it IS one — the
// only differences are that a person made it, it can be forgotten again, and it
// carries NO SPAN.
//
// The missing span is the point, not an omission. A built-in is a region and
// opening it should frame that region; a saved waypoint is a POSITION, and the
// width to view it at is a decision the operator already made with the rung
// buttons. Storing the width that happened to be set the day it was saved, and
// then reimposing it months later, is a bookmark overruling a live choice. Rows
// saved before this carry a span and keep it — no migration, and nobody's list
// changes under them.
//
// Identity used to be a canonical "theater key" derived by snapping the centre to
// a coarse grid, back when the key also had to make a server-side tile cache
// dedupe. Nothing caches per-waypoint any more, so the key earned nothing and cost
// the user precision — two genuinely different blocks 30 nm apart collapsed onto
// one entry. A saved waypoint now keeps the exact coordinates it was saved at and
// carries an opaque id.
//
// Storage is localStorage: it needs no migration, and losing a bookmark is a small
// loss. The cost must be stated where the user can see it — this list is
// per-browser and does not follow them to the vessel.

import type { Waypoint } from "./waypoints.js";

// The key keeps its old name through the rename. It is invisible to the user, and
// spelling it afresh would empty every saved list in the field to buy nothing.
const KEY = "seastate_areas_v2";
const MAX = 60;

export interface SavedWaypoint extends Waypoint {
  /** Epoch ms — most recently opened floats to the top. */
  usedAt: number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Storage is user-editable and outlives deploys, so every field is checked. */
function parseRow(v: unknown): SavedWaypoint | null {
  if (!isRecord(v)) return null;
  const { id, name, note, lat, lon, spanNm, usedAt } = v;
  if (typeof id !== "string" || id === "") return null;
  if (typeof name !== "string" || name === "") return null;
  if (typeof lat !== "number" || !Number.isFinite(lat)) return null;
  if (typeof lon !== "number" || !Number.isFinite(lon)) return null;
  // Absent is a legitimate value now and means "open at the current width", so a
  // missing span is left missing rather than defaulted to 250 nm. Rows written
  // before the change still carry theirs and still open at it.
  const span =
    typeof spanNm === "number" && Number.isFinite(spanNm) && spanNm > 0 ? spanNm : undefined;
  return {
    id,
    name,
    note: typeof note === "string" ? note : "",
    lat,
    lon,
    ...(span === undefined ? {} : { spanNm: span }),
    usedAt: typeof usedAt === "number" && Number.isFinite(usedAt) ? usedAt : 0,
  };
}

function read(): SavedWaypoint[] {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(KEY);
  } catch {
    return []; // storage disabled (private mode, locked-down browser) — not an error
  }
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: SavedWaypoint[] = [];
  const seen = new Set<string>();
  for (const row of parsed) {
    const a = parseRow(row);
    if (a === null || seen.has(a.id)) continue;
    seen.add(a.id);
    out.push(a);
  }
  return out;
}

function write(rows: SavedWaypoint[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(rows.slice(0, MAX)));
  } catch {
    // Quota or disabled storage. Losing a bookmark is not worth an error dialog
    // over the console; the caller's in-memory list still works for this session.
  }
}

/** Most recently used first — the order a list should show. */
export function listSaved(): SavedWaypoint[] {
  return read().sort((a, b) => b.usedAt - a.usedAt);
}

/** Save a new waypoint at an exact position. Returns the stored row. */
export function saveWaypoint(
  name: string,
  lat: number,
  lon: number,
  now: number,
): SavedWaypoint {
  const clean = name.trim().slice(0, 60) || `${lat.toFixed(2)}, ${lon.toFixed(2)}`;
  // A monotonic-ish id from the clock plus a counter suffix: unique enough for a
  // per-browser bookmark list, and readable in storage when debugging.
  const rows = read();
  const id = `s${now.toString(36)}${rows.length.toString(36)}`;
  const row: SavedWaypoint = { id, name: clean, note: "saved waypoint", lat, lon, usedAt: now };
  rows.unshift(row);
  write(rows);
  return row;
}

export function renameWaypoint(id: string, name: string): void {
  const rows = read();
  const row = rows.find((r) => r.id === id);
  if (row === undefined) return;
  row.name = name.trim().slice(0, 60) || row.name;
  write(rows);
}

export function removeWaypoint(id: string): void {
  write(read().filter((r) => r.id !== id));
}

/** Stamp a waypoint as just opened, so the list floats it to the top. */
export function touchWaypoint(id: string, now: number): void {
  const rows = read();
  const row = rows.find((r) => r.id === id);
  if (row === undefined) return;
  row.usedAt = now;
  write(rows);
}
